import {
  BASE_CANVAS_HEIGHT,
  DEFAULT_STORY_DEFAULTS,
  TStoryDefaults,
} from 'context/StoryDefaultsProvider';
import { DEFAULT_PREVIEW_DEVICE } from 'constants/previewDevices';
import { DEFAULT_STORY_DURATION_SECONDS } from 'constants/storyDuration';
import { DEFAULT_STORY_LOCALE } from 'constants/storyLocales';

import {
  BASE_STYLE,
  COUNTDOWN_DEFAULT_LEAD_MS,
  CTA_SIDE_MARGIN,
  DEFAULT_WIDGET_PREVIEW,
  ELEMENT_DEFAULTS,
  HISTORY_LIMIT,
  isMediaType,
  isWidgetType,
  PRESET_TYPE,
  TEXT_ROLES,
  TYPOGRAPHIC_FIELDS,
} from './StudioEditorProvider.constants';
import {
  TElementPreset,
  TElementStyle,
  TElementType,
  TFrameTranslation,
  TStoryElement,
  TStoryFrame,
  TStudioContextValue,
  TStudioDocument,
  TStudioState,
} from './StudioEditorProvider.types';

export const getFitWidthBox = (): { x: number; width: number } => ({
  x: CTA_SIDE_MARGIN,
  width: 100 - 2 * CTA_SIDE_MARGIN,
});

/**
 * Drops `undefined` values so a partial patch merges instead of blanking its siblings — the
 * frame-cover patch is destructured into two targets, and each half must stay partial.
 */
export const definedOnly = <T extends object>(source: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(source).filter(([, value]) => value !== undefined)
  ) as Partial<T>;

const startCase = (value: string): string =>
  value.replace(/(^|[-_])(\w)/g, (_, __, chr: string) => ` ${chr.toUpperCase()}`).trim();

const isBrandedCta = (type: TElementType): boolean => type === 'button';

const pxToFramePercent = (px: number): number => (px / BASE_CANVAS_HEIGHT) * 100;

/**
 * Applied when an element is CREATED, never when one is read back from the wire: an authored
 * element may legitimately carry a field this would strip (media `borderRadius` renders), and the
 * read path has to be the identity or every reload would rewrite the slide.
 */
const applyStyleDefaults = (
  style: TElementStyle,
  type: TElementType,
  preset: TElementPreset,
  storyDefaults: TStoryDefaults
): TElementStyle => {
  const next: TElementStyle = { ...style, fontFamily: storyDefaults.fontFamily };
  if (type === 'text') {
    delete next.backgroundColor;
  }
  if (isMediaType(type) || type === 'shape' || type === 'emoji') {
    TYPOGRAPHIC_FIELDS
      // An emoji's size IS its font size — the one typographic field it keeps.
      .filter((field) => !(type === 'emoji' && field === 'fontSize'))
      .forEach((field) => {
        delete next[field];
      });
  }
  if (isMediaType(type)) {
    delete next.borderRadius;
  }
  /*
   * Interactive elements inherit nothing: their appearance is `widgetVariant` alone, no control
   * exposes an element style for them, and the wire drops the field entirely. Without this they
   * kept all of `BASE_STYLE` — including the `#19E3CF` teal that only buttons ever wanted.
   */
  if (isWidgetType(type)) {
    delete next.backgroundColor;
    delete next.borderRadius;
    TYPOGRAPHIC_FIELDS.forEach((field) => {
      delete next[field];
    });
  }
  // The text role survives only this far: it picks a theme size, then it is gone.
  if (type === 'text' && TEXT_ROLES.has(preset)) {
    next.fontSize = storyDefaults.textSizes[preset as keyof TStoryDefaults['textSizes']];
    next.color = storyDefaults.textColor;
  }
  if (isBrandedCta(type)) {
    next.backgroundColor = storyDefaults.accentColor;
    next.color = storyDefaults.cta.color;
    next.borderRadius = storyDefaults.cta.borderRadius;
  }
  return next;
};

export const createElement = (
  preset: TElementPreset,
  zIndex: number,
  storyDefaults: TStoryDefaults = DEFAULT_STORY_DEFAULTS
): TStoryElement => {
  const type = PRESET_TYPE[preset];
  const defaults = ELEMENT_DEFAULTS[preset] ?? ELEMENT_DEFAULTS['*'];

  const content = defaults.content ?? (isWidgetType(type) ? startCase(type) : undefined);

  return {
    id: crypto.randomUUID(),
    type,
    x: defaults.x,
    y: defaults.y,
    width: defaults.width,
    height: isBrandedCta(type) ? pxToFramePercent(storyDefaults.cta.height) : defaults.height,
    rotation: 0,
    zIndex,
    content,
    src: defaults.src,
    fitWidth: type === 'button' ? true : undefined,
    question: defaults.question,
    options: defaults.options ? [...defaults.options] : undefined,
    correctIndex: defaults.correctIndex,
    reveal: defaults.reveal,
    emoji: defaults.emoji,
    results: defaults.results,
    emojis: defaults.emojis ? [...defaults.emojis] : undefined,
    targetAt:
      type === 'countdown'
        ? new Date(Date.now() + COUNTDOWN_DEFAULT_LEAD_MS).toISOString()
        : undefined,
    countdownStyle: defaults.countdownStyle,
    endedText: defaults.endedText,
    style: applyStyleDefaults({ ...BASE_STYLE, ...defaults.style }, type, preset, storyDefaults),
  };
};

export const createFrame = (
  storyDefaults: TStoryDefaults = DEFAULT_STORY_DEFAULTS
): TStoryFrame => ({
  id: crypto.randomUUID(),
  background: storyDefaults.frameBackground,
  durationSeconds: DEFAULT_STORY_DURATION_SECONDS,
  elements: [],
});

export const createInitialState = (
  initialFrames?: TStoryFrame[],
  storyDefaults: TStoryDefaults = DEFAULT_STORY_DEFAULTS
): TStudioState => {
  const frames =
    initialFrames && initialFrames.length > 0 ? initialFrames : [createFrame(storyDefaults)];
  return {
    frames,
    selectedFrameId: frames[0].id,
    selectedElementId: null,
    activeMenu: 'text',
    previewDevice: DEFAULT_PREVIEW_DEVICE,
    editingLocale: DEFAULT_STORY_LOCALE,
    clipboard: null,
    widgetPreviews: {},
    animationTicks: {},
    past: [],
    future: [],
  };
};

const INITIAL_STATE: TStudioState = createInitialState();

const noop = () => undefined;

export const INITIAL_CONTEXT: TStudioContextValue = {
  ...INITIAL_STATE,
  canUndo: false,
  canRedo: false,
  canPaste: false,
  isDirty: false,
  selectedElement: null,
  selectedFrame: null,
  getWidgetPreview: () => DEFAULT_WIDGET_PREVIEW,
  getAnimationTick: () => 0,
  attributeKeys: [],
  sampleMembers: [],
  setActiveMenu: noop,
  setPreviewDevice: noop,
  setEditingLocale: noop,
  setWidgetPreview: noop,
  playAnimation: noop,
  selectFrame: noop,
  selectElement: noop,
  addElement: noop,
  updateElement: noop,
  updateElementStyle: noop,
  moveElement: noop,
  resizeElement: noop,
  rotateElement: noop,
  deleteElement: noop,
  duplicateElement: noop,
  copyElement: noop,
  cutElement: noop,
  pasteElement: noop,
  addFrame: noop,
  duplicateFrame: noop,
  deleteFrame: noop,
  moveFrame: noop,
  setFrameBackground: noop,
  setFrameCover: noop,
  updateFrameCover: noop,
  removeFrameCover: noop,
  resetFrameLocale: noop,
  resetFrameDuration: noop,
  undo: noop,
  redo: noop,
};

export const snapshot = (state: TStudioState): TStudioDocument =>
  structuredClone({
    frames: state.frames,
    selectedFrameId: state.selectedFrameId,
    selectedElementId: state.selectedElementId,
  });

export const commit = (state: TStudioState, next: Partial<TStudioDocument>): TStudioState => ({
  ...state,
  ...next,
  past: [...state.past, snapshot(state)].slice(-HISTORY_LIMIT),
  future: [],
});

export const getSelectedFrame = (state: TStudioState): TStoryFrame | undefined =>
  state.frames.find((frame) => frame.id === state.selectedFrameId);

export const mapSelectedFrameElements = (
  state: TStudioState,
  updater: (elements: TStoryElement[]) => TStoryElement[]
): TStoryFrame[] =>
  state.frames.map((frame) =>
    frame.id === state.selectedFrameId ? { ...frame, elements: updater(frame.elements) } : frame
  );

export const patchElement = (
  state: TStudioState,
  elementId: string,
  patch: (element: TStoryElement) => TStoryElement
): TStoryFrame[] =>
  mapSelectedFrameElements(state, (elements) =>
    elements.map((element) => (element.id === elementId ? patch(element) : element))
  );

/**
 * Writes a backdrop change to whichever layer the author is editing.
 *
 * Under English it patches the frame. Under any other locale it patches that locale's
 * `translations` bucket, seeding the bucket from the frame first so an author who only wanted to
 * swap the cover image does not lose the fill colour that went with it.
 *
 * Call sites stay locale-agnostic: the locale comes from `state.editingLocale`, so every existing
 * background control keeps working unchanged.
 */
export const patchFrameBackdrop = (
  state: TStudioState,
  frameId: string,
  patch: (backdrop: TFrameTranslation) => TFrameTranslation
): TStoryFrame[] => {
  const locale = state.editingLocale;

  return state.frames.map((frame) => {
    if (frame.id !== frameId) {
      return frame;
    }
    if (locale === DEFAULT_STORY_LOCALE) {
      const { background, cover } = patch({ background: frame.background, cover: frame.cover });
      return { ...frame, background, cover };
    }
    const seed = frame.translations?.[locale] ?? {
      background: frame.background,
      cover: frame.cover,
    };
    return { ...frame, translations: { ...frame.translations, [locale]: patch(seed) } };
  });
};

export const cloneFrameWithNewIds = (frame: TStoryFrame): TStoryFrame => ({
  ...structuredClone(frame),
  id: crypto.randomUUID(),
  elements: frame.elements.map((element) => ({
    ...structuredClone(element),
    id: crypto.randomUUID(),
  })),
});

export const cloneElementForPaste = (source: TStoryElement, zIndex: number): TStoryElement => ({
  ...structuredClone(source),
  id: crypto.randomUUID(),
  x: source.x + 3,
  y: source.y + 3,
  zIndex,
});
